import React, { useMemo, useState } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Box,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import {
  emailValidationError,
  isValidMobileDigits,
  mobileDigitsOnly,
  mobileValidationError,
  normalizeEmail,
} from '@uniqcamp/shared';

const ROLES = ['PRINCIPAL', 'ADMIN', 'MANAGER', 'TEACHER', 'PARENT', 'BUS_DRIVER', 'SECURITY_GUARD', 'ESCORT', 'COORDINATOR', 'CAB_DRIVER'];

const STAFF_ROLES_REQUIRING_EMAIL = ['PRINCIPAL', 'ADMIN', 'MANAGER', 'TEACHER', 'BUS_DRIVER', 'SECURITY_GUARD', 'ESCORT', 'COORDINATOR', 'CAB_DRIVER'];

interface SchoolsListResponse {
  schools: { _id: string; name?: string; uniqueCode?: string }[];
}

interface UserRow {
  _id: string;
  name?: string;
  mobile?: string;
  email?: string;
  role?: string;
  schoolId?: string | { name?: string; uniqueCode?: string };
  isBlocked?: boolean;
  createdAt?: string;
}

interface UsersListResponse {
  users: UserRow[];
}

const Users: React.FC = () => {
  const queryClient = useQueryClient();
  const [schoolIdFilter, setSchoolIdFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRow | null>(null);
  const [form, setForm] = useState({ name: '', mobile: '', email: '', role: 'TEACHER', schoolId: '' });

  const { data: schoolsData } = useQuery<SchoolsListResponse>('schools', async () => {
    const res = await apiClient.get('/schools');
    return res.data as SchoolsListResponse;
  });
  const schools = schoolsData?.schools ?? [];

  const queryParams = new URLSearchParams();
  if (schoolIdFilter) queryParams.set('schoolId', schoolIdFilter);
  if (roleFilter) queryParams.set('role', roleFilter);

  const { data: usersData, isLoading } = useQuery<UsersListResponse>(
    ['users', schoolIdFilter, roleFilter],
    async () => {
      const res = await apiClient.get(`/users?${queryParams.toString()}`);
      return res.data as UsersListResponse;
    }
  );
  const users = useMemo(() => {
    const list = usersData?.users ?? [];
    return [...list].sort((a, b) => {
      const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (aCreated !== bCreated) return bCreated - aCreated;
      return String(b._id).localeCompare(String(a._id));
    });
  }, [usersData?.users]);

  interface CreateUserPayload {
    name: string;
    mobile: string;
    email: string;
    role: string;
    schoolId: string;
  }

  const createMutation = useMutation(
    async (payload: CreateUserPayload) => {
      const res = await apiClient.post('/users', payload);
      return res.data;
    },
    { onSuccess: () => { queryClient.invalidateQueries('users'); setOpenDialog(false); resetForm(); } }
  );
  const updateMutation = useMutation(
    async ({ userId, payload }: { userId: string; payload: Record<string, unknown> }) => {
      const res = await apiClient.put(`/users/${userId}`, payload);
      return res.data;
    },
    { onSuccess: () => { queryClient.invalidateQueries('users'); setOpenDialog(false); setEditingUser(null); resetForm(); } }
  );
  const blockMutation = useMutation(
    async ({ userId, block }: { userId: string; block: boolean }) => {
      const res = await apiClient.put(`/users/${userId}/block`, { block });
      return res.data;
    },
    { onSuccess: () => queryClient.invalidateQueries('users') }
  );

  function resetForm() {
    setForm({ name: '', mobile: '', email: '', role: 'TEACHER', schoolId: '' });
  }

  const handleOpenAdd = () => {
    setEditingUser(null);
    resetForm();
    setOpenDialog(true);
  };

  const handleOpenEdit = (user) => {
    setEditingUser(user);
    setForm({
      name: user.name || '',
      mobile: user.mobile || '',
      email: user.email || '',
      role: user.role || 'TEACHER',
      schoolId: user.schoolId?._id || user.schoolId || '',
    });
    setOpenDialog(true);
  };

  const handleSubmit = () => {
    if (!form.name?.trim() || !form.mobile?.trim()) return;
    const mobErr = mobileValidationError(form.mobile, { required: true, label: 'Mobile' });
    if (mobErr) {
      window.alert(mobErr);
      return;
    }
    const emailRequired = !editingUser && STAFF_ROLES_REQUIRING_EMAIL.includes(form.role);
    const emailErr = emailValidationError(form.email, { required: emailRequired, label: 'Email' });
    if (emailErr) {
      window.alert(emailErr);
      return;
    }
    const mobileNorm = mobileDigitsOnly(form.mobile);
    const emailNorm = form.email?.trim() ? normalizeEmail(form.email) : undefined;
    if (editingUser) {
      updateMutation.mutate({
        userId: editingUser._id,
        payload: { name: form.name.trim(), mobile: mobileNorm, email: emailNorm, role: form.role, schoolId: form.schoolId || undefined },
      });
    } else {
      createMutation.mutate({
        name: form.name.trim(),
        mobile: mobileNorm,
        email: emailNorm,
        role: form.role,
        schoolId: form.schoolId || undefined,
      });
    }
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="h5" component="h1" fontWeight={600}>Users</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>Add User</Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select size="small" label="School" value={schoolIdFilter} onChange={(e) => setSchoolIdFilter(e.target.value)} sx={{ minWidth: 200 }}>
          <MenuItem value="">All schools</MenuItem>
          {schools.map((s) => (
            <MenuItem key={s._id} value={s._id}>{s.name} ({s.uniqueCode})</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">All roles</MenuItem>
          {ROLES.map((r) => (
            <MenuItem key={r} value={r}>{r}</MenuItem>
          ))}
        </TextField>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>School</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={String(u._id)}>
                <TableCell>{u.name}</TableCell>
                <TableCell>{u.mobile}</TableCell>
                <TableCell>{u.email || '–'}</TableCell>
                <TableCell><Chip label={u.role} size="small" /></TableCell>
                <TableCell>{u.schoolId && typeof u.schoolId === 'object' ? `${u.schoolId.name ?? ''} (${u.schoolId.uniqueCode ?? ''})` : u.schoolId ? String(u.schoolId) : '–'}</TableCell>
                <TableCell>
                  {u.isBlocked ? <Chip label="Blocked" color="error" size="small" /> : <Chip label="Active" color="success" size="small" />}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenEdit(u)}><EditIcon /></IconButton>
                  <IconButton size="small" onClick={() => blockMutation.mutate({ userId: u._id, block: !u.isBlocked })} title={u.isBlocked ? 'Unblock' : 'Block'}>
                    <BlockIcon color={u.isBlocked ? 'success' : 'error'} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); setEditingUser(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            <TextField label="Mobile" required value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} />
            <TextField label="Email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            <TextField select label="Role" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
              {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            <TextField select label="School" value={form.schoolId} onChange={(e) => setForm((f) => ({ ...f, schoolId: e.target.value }))}>
              <MenuItem value="">–</MenuItem>
              {schools.map((s) => <MenuItem key={s._id} value={s._id}>{s.name} ({s.uniqueCode})</MenuItem>)}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !form.name?.trim() ||
              !isValidMobileDigits(form.mobile) ||
              Boolean(
                emailValidationError(form.email, {
                  required: !editingUser && STAFF_ROLES_REQUIRING_EMAIL.includes(form.role),
                }),
              ) ||
              createMutation.isLoading ||
              updateMutation.isLoading
            }
          >
            {editingUser ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
